import { useState, useEffect } from "react";
import { DayPicker } from "react-day-picker";
import { add, addDays, eachDayOfInterval, isBefore, isEqual, isSameDay, set, subDays } from "date-fns";
import {
    af,
    ar,
    az,
    be,
    bg,
    bn,
    bs,
    ca,
    cs,
    cy,
    da,
    de,
    el,
    eo,
    es,
    et,
    eu,
    fi,
    fr,
    gl,
    gu,
    he,
    hi,
    hr,
    hu,
    hy,
    id,
    is,
    it,
    ja,
    ka,
    kn,
    ko,
    lb,
    lt,
    lv,
    mk,
    ms,
    mt,
    nb,
    nl,
    nn,
    pl,
    ro,
    ru,
    sk,
    sl,
    sq,
    sr,
    sv,
    ta,
    te,
    th,
    tr,
    uk,
    vi,
    pt,
    zhCN,
    zhHK,
    zhTW,
    ptBR,
} from "date-fns/locale";
import { Drawer, Button, Text, Icons } from "../../design";
import { translate } from "../../utils/translations";
import { RDP_CSS } from "../../utils/constants";
import { useLoop } from "../loop-provider";
import { useAxios } from "../../utils/helpers";

function getDateFnsLocale(locale: string) {
    const map = {
        es: es,
        fr: fr,
        de: de,
        cs: cs,
        it: it,
        pl: pl,
        ru: ru,
        tr: tr,
        uk: uk,
        ja: ja,
        ko: ko,
        ar: ar,
        af: af,
        az: az,
        be: be,
        bg: bg,
        bn: bn,
        bs: bs,
        ca: ca,
        cy: cy,
        da: da,
        el: el,
        eo: eo,
        et: et,
        eu: eu,
        fi: fi,
        gl: gl,
        gu: gu,
        he: he,
        hi: hi,
        hr: hr,
        hu: hu,
        hy: hy,
        id: id,
        is: is,
        ka: ka,
        kn: kn,
        lb: lb,
        lt: lt,
        lv: lv,
        mk: mk,
        ms: ms,
        mt: mt,
        nb: nb,
        nl: nl,
        nn: nn,
        sk: sk,
        sl: sl,
        sq: sq,
        sr: sr,
        sv: sv,
        ta: ta,
        te: te,
        th: th,
        vi: vi,
        ro: ro,
        pt: pt,
        // "pt-BR": ptBR,
        // "zh-CN": zhCN,
        // "zh-HK": zhHK,
        // "zh-TW": zhTW,
    };
    return map[locale] || null;
}

function SelectDateDrawer(props: any) {
    const DAYS = 180;
    const [axios] = useAxios();
    const { deliveryPreferences, myshopify_domain, locale } = useLoop();
    const [selectedDate, setselectedDate] = useState(new Date(props.currentDate * 1000));
    const [disabledDates, setdisabledDates] = useState([]);
    const [zapietDatesLoading, setzapietDatesLoading] = useState(false);
    const [minDate, setminDate] = useState(addDays(new Date(), 1));
    const [maxDate, setmaxDate] = useState(add(new Date(), { months: 3 }));

    const findDisabledDates = () => {
        let _disabledDates = [];
        let startDate = addDays(new Date(), 1);
        let endDate = addDays(new Date(), 180);
        if (props.billing_policy) {
            if (props.billing_policy.anchor_type) {
                if (props.billing_policy.anchor_type == "WEEKDAY") {
                    while (isBefore(startDate, endDate) || isEqual(startDate, endDate)) {
                        if (startDate.getDay() != props.billing_policy.anchor_day) {
                            _disabledDates.push(startDate);
                        }
                        startDate = addDays(startDate, 1);
                    }
                } else if (props.billing_policy.anchor_type == "MONTHDAY") {
                    while (isBefore(startDate, endDate) || isEqual(startDate, endDate)) {
                        if (startDate.getDate() != props.billing_policy.anchor_day) {
                            _disabledDates.push(startDate);
                        }
                        startDate = addDays(startDate, 1);
                    }
                } else if (props.billing_policy.anchor_type == "YEARDAY") {
                    while (isBefore(startDate, endDate) || isEqual(startDate, endDate)) {
                        if (
                            startDate.getMonth() != props.billing_policy.anchor_month - 1 &&
                            startDate.getDate() != props.billing_policy.anchor_day
                        ) {
                            _disabledDates.push(startDate);
                        }
                        startDate = addDays(startDate, 1);
                    }
                }
            }
        }
        return _disabledDates;
    };

    const findZapietDisabledDates = (dates) => {
        let _disabledDates = [];
        if (dates?.disableDays?.length) {
            for (let _disableDay of dates?.disableDays) {
                let startDate = addDays(new Date(), 1);
                let endDate = addDays(new Date(), DAYS);
                while (isBefore(startDate, endDate) || isEqual(startDate, endDate)) {
                    if (_disableDay === 7) {
                        _disableDay = 0;
                    }
                    if (startDate.getDay() === _disableDay) {
                        _disabledDates.push(startDate);
                    }
                    startDate = addDays(startDate, 1);
                }
            }
        }
        if (dates?.disabledDates?.length) {
            for (const _date of dates?.disabledDates) {
                _disabledDates.push(new Date(_date));
            }
        }
        if (dates?.enabledDates?.length) {
            let _enabledDates = [];
            let startDate = addDays(new Date(), 1);
            let endDate = addDays(new Date(), DAYS);
            const datesBetween = eachDayOfInterval({
                start: startDate,
                end: endDate,
            });
            for (const _date of dates?.enabledDates) {
                _enabledDates.push(set(new Date(), { year: _date[0], month: _date[1], date: _date[2] }));
            }
            _disabledDates = [
                ...datesBetween.filter((date1) => !_enabledDates.some((date2) => isSameDay(date1, date2))),
            ];
        }
        return _disabledDates;
    };

    const handleConfirm = async () => {
        props.handleDateSelect(selectedDate.getTime() / 1000);
        props.toggleActive();
    };

    const loadZapietDates = async () => {
        setzapietDatesLoading(true);
        const _payload = {
            shopifyDomain: myshopify_domain,
        };
        let _zapietDisabledDates = [];
        let _anchorDisabledDates = [];
        try {
            const response: any = await axios.post(
                `${process.env.NEXT_PUBLIC_LOOP_BACKEND}/api/customer/v2/zapiet/datesConfig`,
                _payload,
            );
            const data = response?.data?.data;
            if (data && Object.entries(data).length) {
                if (data?.minDate) {
                    const _minDate = subDays(data?.minDate, 1);
                    setminDate(new Date(_minDate));
                    setminDate(new Date(data?.minDate));
                }
                _zapietDisabledDates = findZapietDisabledDates(data);
                _anchorDisabledDates = findDisabledDates();
                setdisabledDates([..._zapietDisabledDates, ..._anchorDisabledDates]);
            } else {
                _anchorDisabledDates = findDisabledDates();
                setdisabledDates([..._anchorDisabledDates]);
            }
        } catch (error) {}
        setzapietDatesLoading(false);
    };

    useEffect(() => {
        getDisableBeforeDates();
        if (myshopify_domain) {
            loadZapietDates();
        }
        findDisabledDates();
    }, []);

    const getDisableBeforeDates = () => {
        let _chargeOffset = undefined;
        for (const _pref of deliveryPreferences) {
            if (
                _pref.defaultPreferenceKey === "delivery_scheduled_order" &&
                _pref.storePreferenceEnabled &&
                _pref.inputValueInt
            ) {
                _chargeOffset = _pref.inputValueInt;
                break;
            } else {
                _chargeOffset = 1;
            }
        }
        setminDate(addDays(new Date(), _chargeOffset));
    };

    return (
        <Drawer
            id="subscription-create-start-date"
            title={translate("subscription_create_drawer_start_date_header")}
            active={props.active}
            toggleActive={props.toggleActive}
        >
            <style>{RDP_CSS}</style>
            {zapietDatesLoading ? (
                <Icons.Spinner />
            ) : (
                <>
                    <Drawer.Body>
                        <div className="my-2">
                            <Text.P1>{translate("subscription_create_drawer_start_date_body")}</Text.P1>
                        </div>
                        <div className="w-100 d-flex justify-content-center">
                            <DayPicker
                                mode="single"
                                selected={selectedDate}
                                onSelect={setselectedDate}
                                disabled={[{ before: minDate }, ...disabledDates]}
                                locale={getDateFnsLocale(locale.split("-")[0])}
                            />
                        </div>
                    </Drawer.Body>
                    <Drawer.Footer>
                        <div className="d-flex flex-column">
                            <Button size="lg" className="m-2" onClick={handleConfirm}>
                                {translate("subscription_create_drawer_start_date_confirm_button")}
                            </Button>
                            <Button size="lg" variant="underline" className="m-2" onClick={props.toggleActive}>
                                {translate("go_back_button")}
                            </Button>
                        </div>
                    </Drawer.Footer>
                </>
            )}
        </Drawer>
    );
}

export default SelectDateDrawer;
